/**
 * Optimizes image URLs for better performance and responsive loading
 */

export const optimizeImageUrl = (
  url: string, 
  options?: { width?: number; quality?: number }
): string => {
  if (!url) return '';
  
  // If it's already an optimized URL or external service, return as is
  if (url.includes('cloudinary') || url.includes('imgix') || url.includes('unsplash')) {
    return url;
  }
  
  // For local images or basic URLs, return as is for now
  // In production, you might want to integrate with an image optimization service
  return url;
};

export const generateImageSrcSet = (
  url: string,
  sizes: number[] = [320, 640, 768, 1024, 1280, 1920]
): string => {
  if (!url) return '';
  
  return sizes
    .map(size => `${optimizeImageUrl(url, { width: size })} ${size}w`)
    .join(', ');
};

export const getOptimalImageWidth = (containerWidth?: number): number => {
  // If no container width provided, use viewport width
  const width = containerWidth || window.innerWidth;
  
  // Return the next power of 2 or common breakpoint
  const breakpoints = [320, 640, 768, 1024, 1280, 1920];
  
  for (const breakpoint of breakpoints) {
    if (width <= breakpoint) {
      return breakpoint;
    }
  }
  
  return 1920; // Max width
};

export const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
};

export const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
};